import type { Decimal } from '../math/num';
import { D, mul, pow } from '../math/num';

/**
 * The Ascension ladder (CONCEPT §5) as data. M1 only instantiates the first
 * frame; the rest are here so M4's prestige is a data climb, not new code.
 * Graph capacity (node slots) grows with the chassis — the Mind sprawls as you
 * upload into bigger bodies.
 */
export interface FrameDef {
  id: string;
  tier: number;
  name: string;
  subtitle: string;
  baseHp: number;
  /** HP per second regenerated, as a fraction of max HP. */
  regenFrac: number;
  /** Scrap cost to take the frame from `level` to `level+1`. */
  levelCostBase: number;
  levelCostGrowth: number;
  /** Node slots available in the Mind at this chassis. */
  nodeSlots: number;
  /** Base Weight budget for equipped parts (grows with chassis). */
  weightBudget: number;
}

/**
 * The 12-rung transformative ladder. Each tier is a DIFFERENT machine archetype
 * (gremlin → android → caged pilot-loader → chicken-walker → crab tank → war
 * knight → gorilla engine → siege centipede → skyscraper crane → walking castle
 * → ambulatory city → star-hearted god engine) — not a recolor of the last one.
 * Original 6 defIds are unchanged so existing saves slot into the denser ladder.
 */
export const FRAMES: FrameDef[] = [
  { id: 'frame.scrapbot', tier: 1, name: 'Scrap-Bot', subtitle: 'a hunched gremlin of taped-together junk', baseHp: 100, regenFrac: 0.008, levelCostBase: 10, levelCostGrowth: 1.14, nodeSlots: 6, weightBudget: 12 },
  { id: 'frame.biped', tier: 2, name: 'Biped', subtitle: 'an upright android. Finally, posture', baseHp: 420, regenFrac: 0.009, levelCostBase: 42, levelCostGrowth: 1.14, nodeSlots: 8, weightBudget: 16 },
  { id: 'frame.exo', tier: 3, name: 'Exo-Frame', subtitle: 'the Biped, bolted into a hydraulic power-loader', baseHp: 1800, regenFrac: 0.01, levelCostBase: 180, levelCostGrowth: 1.15, nodeSlots: 10, weightBudget: 22 },
  { id: 'frame.walker', tier: 4, name: 'Walker', subtitle: 'an armored cockpit on enormous bird legs', baseHp: 22000, regenFrac: 0.011, levelCostBase: 2200, levelCostGrowth: 1.16, nodeSlots: 13, weightBudget: 38 },
  { id: 'frame.quad', tier: 5, name: 'Quadzilla', subtitle: 'a four-legged siege crab. never skips leg day', baseHp: 9e4, regenFrac: 0.011, levelCostBase: 9e3, levelCostGrowth: 1.16, nodeSlots: 16, weightBudget: 48 },
  { id: 'frame.titan', tier: 6, name: 'Titan', subtitle: 'a knight-class war mech with shoulders for days', baseHp: 4e5, regenFrac: 0.012, levelCostBase: 4e4, levelCostGrowth: 1.17, nodeSlots: 19, weightBudget: 60 },
  { id: 'frame.juggernaut', tier: 7, name: 'Juggernaut', subtitle: 'a knuckle-walking engine of unreasonable arms', baseHp: 2.2e6, regenFrac: 0.012, levelCostBase: 2e5, levelCostGrowth: 1.17, nodeSlots: 22, weightBudget: 74 },
  { id: 'frame.leviathan', tier: 8, name: 'Leviathan', subtitle: 'a segmented siege serpent. it has no business existing', baseHp: 1.1e7, regenFrac: 0.013, levelCostBase: 1e6, levelCostGrowth: 1.18, nodeSlots: 26, weightBudget: 100 },
  { id: 'frame.colossus', tier: 9, name: 'Colossus', subtitle: 'a skyscraper that learned to walk, cranes and all', baseHp: 7e7, regenFrac: 0.013, levelCostBase: 6e6, levelCostGrowth: 1.18, nodeSlots: 30, weightBudget: 120 },
  { id: 'frame.bastion', tier: 10, name: 'Bastion', subtitle: 'a fortress on six legs. the gate is decorative. mostly', baseHp: 5e8, regenFrac: 0.014, levelCostBase: 5e7, levelCostGrowth: 1.19, nodeSlots: 35, weightBudget: 150 },
  { id: 'frame.metropolis', tier: 11, name: 'Metropolis-Frame', subtitle: 'a city that walks. rent remains too high', baseHp: 4e9, regenFrac: 0.015, levelCostBase: 4e8, levelCostGrowth: 1.19, nodeSlots: 40, weightBudget: 185 },
  { id: 'frame.dyson', tier: 12, name: 'Dyson-Frame', subtitle: 'it has a star where its heart should be', baseHp: 3.5e10, regenFrac: 0.016, levelCostBase: 3.5e9, levelCostGrowth: 1.2, nodeSlots: 46, weightBudget: 225 },
];

export function getFrame(id: string): FrameDef {
  const f = FRAMES.find((x) => x.id === id);
  if (!f) throw new Error(`Unknown frame: ${id}`);
  return f;
}

export function frameByTier(tier: number): FrameDef {
  return FRAMES[Math.min(Math.max(tier - 1, 0), FRAMES.length - 1)] as FrameDef;
}

/** Max HP at a given frame level. */
export function frameMaxHp(frame: FrameDef, level: number): Decimal {
  return mul(D(frame.baseHp), pow(D(1.12), level - 1));
}

/** Global damage multiplier the frame contributes on top of the Mind's output. */
export function frameDamageMult(level: number): Decimal {
  return pow(D(1.08), level - 1);
}

/** Scrap cost to go from `level` to `level+1`. */
export function frameLevelCost(frame: FrameDef, level: number): Decimal {
  return mul(D(frame.levelCostBase), pow(D(frame.levelCostGrowth), level - 1));
}
